import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ScrollText, ShieldCheck, LayoutTemplate } from "lucide-react";
import api from "@/lib/api";
import AgreementsList from "@/components/agreements/AgreementsList";
import AgreementEditorModal from "@/components/agreements/AgreementEditorModal";
import SignedRecordsTable from "@/components/agreements/SignedRecordsTable";
import SignaturesAudit from "@/pages/admin/SignaturesAudit";

const BASE = "/admin/agreements-hub/agreements";
const TARGETS = ["membership_all", "services", "packages", "memberships", "service_types", "locations"];
const TABS = [["master", "Master Agreements", ScrollText], ["signed", "All Signed Records", ShieldCheck], ["templates", "Agreement Templates", LayoutTemplate]];

export default function AgreementsHub() {
  const [tab, setTab] = useState("master");
  const [scope, setScope] = useState("master");
  const [fromTemplate, setFromTemplate] = useState(null);
  const qc = useQueryClient();
  const { data: options = {} } = useQuery({ queryKey: ["agreements-hub-options"], queryFn: () => api.get("/admin/agreements-hub/options").then((r) => r.data) });
  const { data: list = [] } = useQuery({
    queryKey: ["agreements-hub", tab, scope],
    queryFn: () => api.get(BASE, { params: tab === "templates" ? { templates: true } : { scope } }).then((r) => r.data),
    enabled: tab !== "signed",
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["agreements-hub"] });
  const archiveUrl = (a, action) => `${BASE}/${a.id}/${action}`;

  return (
    <div data-testid="agreements-hub">
      <h2 className="font-serif text-3xl text-[#1A242B]">Agreements</h2>
      <p className="text-sm text-[#4A5D6B] mt-1 mb-5">Platform waivers, terms and participation policies. Every saved change is a new version; signatures always point to the exact version signed. Agreements are archived, never deleted.</p>
      <div className="flex gap-2 overflow-x-auto pb-1 mb-6" role="tablist">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`agreements-hub-tab-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm border ${tab === k ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white text-[#144F75] border-[#E5E0D8]"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === "master" && (
        <>
          <div className="flex gap-2 mb-4 text-xs" data-testid="agreements-hub-scope">
            {[["master", "Master"], ["center", "Center agreements"], ["enhancer", "Enhancer agreements"]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} data-testid={`agreements-scope-${k}`} className={`rounded-full px-3 py-1.5 border ${scope === k ? "border-[#144F75] text-[#144F75] bg-[#144F75]/[0.06]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
            ))}
          </div>
          <AgreementsList items={list} base={BASE} archiveUrl={archiveUrl} options={options} targets={TARGETS} canEdit={scope === "master"}
            onChanged={refresh} testid="master-agreements" emptyText={scope === "master" ? "No master agreements yet." : "None created yet."} />
          {scope !== "master" && <p className="text-xs text-[#8FA89B] mt-3">Center and enhancer agreements are managed by their owners; you can review versions here.</p>}
        </>
      )}
      {tab === "signed" && (
        <div className="space-y-10">
          <SignedRecordsTable endpoint="/admin/agreements-hub/signed" types={options.types || []} testid="admin-signed-records" />
          <SignaturesAudit />
        </div>
      )}
      {tab === "templates" && (
        <AgreementsList items={list} base={BASE} archiveUrl={archiveUrl} options={options} targets={[]} template newLabel="New template"
          onChanged={refresh} testid="agreement-templates" emptyText="No templates yet." onUseTemplate={(t) => setFromTemplate(t)} />
      )}
      {fromTemplate && (
        <AgreementEditorModal agreement={null} base={BASE} options={options} targets={TARGETS}
          onClose={() => setFromTemplate(null)} onSaved={() => { setFromTemplate(null); setTab("master"); refresh(); }}
          key={fromTemplate.id} {...{ agreement: { title: fromTemplate.title.replace(/ template$/i, ""), type: fromTemplate.type, content: fromTemplate.content, pdf_path: fromTemplate.pdf_path, pdf_filename: fromTemplate.pdf_filename } }} />
      )}
    </div>
  );
}
